import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Loader2, Check, ListChecks } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

export default function WaitListSignup() {
  const nav = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [items, setItems] = useState([{ title: "", console: "", catalog_id: null }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [consoles, setConsoles] = useState([]);

  useEffect(() => {
    api.get("/consoles").then((r) => setConsoles(r.data)).catch(() => {});
  }, []);

  const updField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updItem = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v, catalog_id: k === "title" ? null : it.catalog_id } : it));
  const addItem = () => setItems((p) => [...p, { title: "", console: "", catalog_id: null }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const valid = items.filter((it) => it.title.trim().length > 0);
      if (valid.length === 0) throw new Error("Add at least one game title.");
      await api.post("/waitlist", { ...form, items: valid });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return <Layout>
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="waitlist-confirm">
        <ListChecks size={56} style={{ color: "var(--ab-primary)" }} className="mx-auto mb-6" />
        <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>You&apos;re on the list</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-4">Got it — we&apos;ll be in touch.</h1>
        <p className="text-sm mb-8" style={{ color: "var(--ab-text-muted)" }}>
          We&apos;ll email you the moment one of your games hits our shelves. You&apos;ll get one notification per game — first come, first served.
        </p>
        <button onClick={() => nav("/")} className="btn-primary" data-testid="waitlist-confirm-home">Back Home</button>
      </div>
    </Layout>;
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="label-eyebrow mb-3" style={{ color: "var(--ab-primary)" }}>Wait List</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-3">Want a game we don&apos;t have?</h1>
        <p className="text-sm max-w-xl mb-8" style={{ color: "var(--ab-text-muted)" }}>
          Tell us what you&apos;re after. The first time we restock a match we&apos;ll email you — one email per title, then you&apos;re off the list.
        </p>
        <form onSubmit={submit} className="surface p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">First Name</label>
              <input className="input-terminal" required value={form.first_name} onChange={(e) => updField("first_name", e.target.value)} data-testid="wl-first-name" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Last Name</label>
              <input className="input-terminal" required value={form.last_name} onChange={(e) => updField("last_name", e.target.value)} data-testid="wl-last-name" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input type="email" className="input-terminal" required value={form.email} onChange={(e) => updField("email", e.target.value)} data-testid="wl-email" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Phone</label>
              <input className="input-terminal" required value={form.phone} onChange={(e) => updField("phone", e.target.value)} placeholder="5551234567" data-testid="wl-phone" />
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Games You Want</label>
            <div className="space-y-3">
              {items.map((it, i) => (
                <ItemRow key={i} index={i} item={it} consoles={consoles}
                         onChange={(k, v) => updItem(i, k, v)}
                         onRemove={items.length > 1 ? () => removeItem(i) : null} />
              ))}
            </div>
            <button type="button" onClick={addItem} className="btn-secondary mt-3 inline-flex items-center gap-2 text-xs" data-testid="wl-add-item">
              <Plus size={12} /> Add another game
            </button>
            <p className="text-[11px] mt-2" style={{ color: "var(--ab-text-dim)" }}>
              Tip — pick from the suggestions when they appear so we&apos;re sure we have the right game. Free-text is fine for obscure titles too.
            </p>
          </div>

          {error && <div className="text-sm" style={{ color: "var(--ab-danger)" }} data-testid="wl-error">{error}</div>}
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="wl-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Join the Wait List
          </button>
        </form>
      </div>
    </Layout>
  );
}

function ItemRow({ index, item, consoles, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!item.title || item.title.length < 2 || item.catalog_id) {
      setSuggestions([]); return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/catalog/search", { params: { q: item.title, limit: 6 } });
        setSuggestions(r.data || []);
        setOpen(true);
      } catch { setSuggestions([]); }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [item.title, item.catalog_id]);

  const pick = (s) => {
    onChange("title", s.title);
    onChange("console", s.console || "");
    onChange("catalog_id", s.id);
    setSuggestions([]); setOpen(false);
  };

  return (
    <div className="surface p-3 flex flex-col sm:flex-row gap-2 items-stretch" data-testid={`wl-item-${index}`}>
      <div className="flex-1 relative">
        <input className="input-terminal" required placeholder="Game title (e.g. Mario Kart 64)"
               value={item.title}
               onChange={(e) => onChange("title", e.target.value)}
               onFocus={() => setOpen(true)}
               onBlur={() => setTimeout(() => setOpen(false), 200)}
               data-testid={`wl-title-${index}`} />
        {open && suggestions.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 surface" style={{ maxHeight: 240, overflow: "auto" }} data-testid={`wl-suggestions-${index}`}>
            {suggestions.map((s) => (
              <button key={s.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-[var(--ab-surface-2)] border-b" style={{ borderColor: "var(--ab-border)" }}>
                <span className="font-bold">{s.title}</span>
                <span className="ml-2 text-[10px]" style={{ color: "var(--ab-text-dim)" }}>{s.console}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <select className="input-terminal sm:max-w-[180px]" value={item.console} onChange={(e) => onChange("console", e.target.value)} data-testid={`wl-console-${index}`}>
        <option value="">Any console</option>
        {consoles.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
      </select>
      {onRemove && (
        <button type="button" onClick={onRemove} className="inline-flex items-center justify-center" style={{ width: 36, border: "1px solid var(--ab-danger)", color: "var(--ab-danger)" }} data-testid={`wl-remove-${index}`}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}
